import { useEffect, useRef } from 'react';
import CasinoMds from '@fanduel/casino-mds';
import type { MDSWebSocketMessage } from '@fanduel/casino-mds-contract';
import { useStore } from '@/store/useStore';
import { usePopup } from '@/features/Popup/usePopup';
import { useInGameMerchFeatureFlag } from '@/features/InGameMerch/useInGameMerchFeatureFlag';
import { initializeUnifiedModules } from '@/unified-modules/unified-modules-core';
import { getBalanceData } from '@/unified-modules/wallet/helpers/get-balance-data';
import { GAME_WINDOW_TO_CLIENT_EVENTS } from '@/event-manager/types';
import useEventManagerEmitter from '@/event-manager/use-event-manager-emiter';

export const MDS_PROMO_MSG_TYPE = 'data:PROMO';
export const INIT_BONUS_ACTIVE_SUBTYPE = 'INIT_BONUS_ACTIVE';

const getPausable = (): boolean =>
  !(useStore.getState().gameContext.unpausable ?? false);

export const useMdsPromoListener = () => {
  const { setInGameMerch } = usePopup();
  const setInGameMerchRef = useRef(setInGameMerch);
  useEffect(() => {
    setInGameMerchRef.current = setInGameMerch;
  }, [setInGameMerch]);

  const { enabled: inGameMerchEnabled, abcKey } = useInGameMerchFeatureFlag();
  const inGameMerchEnabledRef = useRef(inGameMerchEnabled);
  const abcKeyRef = useRef(abcKey);
  useEffect(() => {
    inGameMerchEnabledRef.current = inGameMerchEnabled;
    abcKeyRef.current = abcKey;
  }, [inGameMerchEnabled, abcKey]);

  const { emitClientEvent } = useEventManagerEmitter();
  const emitClientEventRef = useRef(emitClientEvent);
  useEffect(() => {
    emitClientEventRef.current = emitClientEvent;
  }, [emitClientEvent]);

  const userId = useStore((s) => s.userSessionContext.userId);
  const gameLoaded = useStore((s) => s.gameStatus.gameLoadSuccess);
  const gameUid = useStore((s) => s.gameContext.gameUid);
  const gameSwitchInProgress = useStore(
    (s) => s.gameWindowContext.gameSwitchInProgress,
  );
  // gameUid the initial popup was last shown for; undefined = not shown yet.
  const initShownForGameUidRef = useRef<string | null | undefined>(undefined);

  useEffect(() => {
    let cancelled = false;

    initializeUnifiedModules().then(() => {
      if (cancelled) {
        return;
      }

      CasinoMds.addMessageHandler(
        MDS_PROMO_MSG_TYPE,
        (message: MDSWebSocketMessage) => {
          if (!inGameMerchEnabledRef.current) {
            return;
          }

          const promoMessage = message?.payload?.data?.addedMessage;
          if (!promoMessage) {
            return;
          }

          setInGameMerchRef.current({
            subtype: promoMessage.subtype,
            payload: promoMessage.payload,
            createdTs: promoMessage.createdTs,
            pausable: getPausable(),
            abcKey: abcKeyRef.current,
          });

          // Promo messages can change the bonus balance; push the fresh
          // balance to the game.
          getBalanceData((balance) => {
            emitClientEventRef.current(
              GAME_WINDOW_TO_CLIENT_EVENTS.RELOAD_BALANCE,
              balance,
            );
          });
        },
      );
    });

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!userId || !inGameMerchEnabled) {
      return undefined;
    }

    let cancelled = false;

    initializeUnifiedModules().then(() => {
      if (!cancelled) {
        CasinoMds.initSocket(userId);
      }
    });

    return () => {
      cancelled = true;
    };
  }, [userId, inGameMerchEnabled]);

  // Show the initial popup once per game: on first load, after a soft reload
  // (gameLoadSuccess resets) and after a game switch lands a new gameUid. A
  // game switch doesn't reset gameLoadSuccess, so wait for it to finish so the
  // popup targets the new game rather than flashing under the switch overlay.
  useEffect(() => {
    if (!gameLoaded) {
      initShownForGameUidRef.current = undefined;
      return;
    }

    if (
      !userId ||
      !inGameMerchEnabled ||
      gameSwitchInProgress ||
      initShownForGameUidRef.current === gameUid
    ) {
      return;
    }

    initShownForGameUidRef.current = gameUid;
    setInGameMerchRef.current({
      subtype: INIT_BONUS_ACTIVE_SUBTYPE,
      payload: null,
      createdTs: Date.now(),
      pausable: getPausable(),
      abcKey,
    });
  }, [
    userId,
    inGameMerchEnabled,
    abcKey,
    gameLoaded,
    gameUid,
    gameSwitchInProgress,
  ]);
};
